/** docs/UX.md 11 · `/m` 总览 */
import { useMemo } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState, Icon, ProgressBar } from "@xinghai/ui";
import { batchesApi, invitationsApi, qualityApi, speakersApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { ClassDashboard } from "./ClassDashboard";
import { SystemStatusCard } from "./SystemStatusCard";
import { invitationState } from "./UsersPage";
import {
  ErrorNotice,
  GateLight,
  LoadingBlock,
  PageHeader,
  Section,
  StatRow,
  StatusTag,
  formatNumber,
} from "./common";
import {
  BATCH_STATE_ICON,
  BATCH_STATE_LABEL,
  BATCH_STATE_TONE,
  RECORDING_STATE_ICON,
  RECORDING_STATE_LABEL,
  RECORDING_STATE_ORDER,
  TASK_LABEL,
} from "./labels";
import type { QualityAnnotatorsResponse, QualitySummaryResponse } from "./types";

/**
 * `/m` — a 教师 gets the class board and nothing else (docs/UX.md §3.1);
 * 课程团队 / 管理员 get the same board with a class selector, then the
 * corpus-wide overview below it.
 */
export function OverviewPage() {
  const { roles, isLoading } = useAuth();
  const isStaff = roles.includes("course_team") || roles.includes("admin");
  // B8 — `health/deep` and `admin/backups` are admin-only; anyone else simply
  // does not see the card rather than being shown a 403.
  const isAdmin = roles.includes("admin");

  if (isLoading) {
    return (
      <div className="xz-adm">
        <PageHeader title="总览" />
        <LoadingBlock rows={3} label="正在加载" />
      </div>
    );
  }

  if (!isStaff) return <ClassDashboard />;

  return (
    <div className="xz-adm">
      <PageHeader
        title="总览"
        description="先看班级情况，下面是全库的采集、标注与质量数据。"
        actions={
          <Link className="gz-button gz-button--primary" to="/m/batches">
            去批次与派发
          </Link>
        }
      />
      <ClassDashboard showClassSelector embedded />
      {isAdmin && <SystemStatusCard />}
      <StaffOverview />
    </div>
  );
}

function StaffOverview() {
  const batches = useQuery({
    queryKey: qk.batches,
    queryFn: ({ signal }) => batchesApi.list(signal),
  });
  const summary = useQuery({
    queryKey: qk.qualitySummary,
    queryFn: ({ signal }) => qualityApi.summary<QualitySummaryResponse>(signal),
  });
  const annotators = useQuery({
    queryKey: qk.qualityAnnotators,
    queryFn: ({ signal }) => qualityApi.annotators<QualityAnnotatorsResponse>(signal),
  });

  // ACCOUNTS.md §6 / §2 — 待办：待核验学生、未使用的邀请。
  const unverified = useQuery({
    queryKey: qk.speakersFiltered({ verified: 0 }),
    queryFn: ({ signal }) => speakersApi.list({ verified: 0 }, signal),
  });
  const invitations = useQuery({
    queryKey: qk.adminInvitations,
    queryFn: ({ signal }) => invitationsApi.list(signal),
    retry: false,
  });

  const unverifiedCount =
    unverified.data?.speakers.filter((s) => s.verified !== true).length ?? null;
  // One derivation of the invitation state, shared with 账号 (`invitationState`
  // prefers the server's own `status` over re-deriving it from the timestamps).
  const pendingInvites =
    invitations.data?.invitations.filter((i) => invitationState(i) === "pending").length ?? null;

  const stateStats = useMemo(() => {
    const counts = summary.data?.recordings_by_state ?? {};
    return RECORDING_STATE_ORDER.map((s) => ({
      key: s,
      icon: RECORDING_STATE_ICON[s],
      label: RECORDING_STATE_LABEL[s],
      value: counts[s] ?? 0,
    }));
  }, [summary.data]);

  const gates = summary.data?.gates;
  const overall = summary.data?.overall;

  return (
    <>
      {/* --- 待办 ------------------------------------------------------- */}
      <Section title="待办" subtitle="需要课程团队或管理员处理的事项">
        <div className="xz-adm__cards">
          <Link className="xz-bcard" to="/m/speakers">
            <div className="xz-adm__tags">
              <StatusTag icon="ui-clock" tone={unverifiedCount ? "warning" : "neutral"}>
                待核验学生
              </StatusTag>
            </div>
            <p className="xz-bcard__title gz-type-score">
              {unverified.isPending ? "…" : unverified.isError ? "—" : unverifiedCount}
            </p>
            <p className="xz-bcard__meta">
              <span>自助注册的学生需要核对方言与水平后才算入册。</span>
            </p>
          </Link>

          <Link className="xz-bcard" to="/m/users">
            <div className="xz-adm__tags">
              <StatusTag icon="ui-key" tone={pendingInvites ? "info" : "neutral"}>
                待使用邀请
              </StatusTag>
            </div>
            <p className="xz-bcard__title gz-type-score">
              {invitations.isPending ? "…" : invitations.isError ? "—" : pendingInvites}
            </p>
            <p className="xz-bcard__meta">
              <span>已发出但还没有人使用的一次性邀请；过期会自动失效。</span>
            </p>
          </Link>

          <Link className="xz-bcard" to="/m/classes">
            <div className="xz-adm__tags">
              <StatusTag icon="ui-school">班级邀请码</StatusTag>
            </div>
            <p className="xz-bcard__title">管理班级</p>
            <p className="xz-bcard__meta">
              <span>发放、轮换或停用学生自助注册用的班级邀请码。</span>
            </p>
          </Link>
        </div>
      </Section>

      {/* --- 批次 ------------------------------------------------------- */}
      <Section title="批次" subtitle="按状态查看采集与标注进度">
        {batches.isPending ? (
          <LoadingBlock rows={2} label="正在加载批次" />
        ) : batches.isError ? (
          <ErrorNotice
            error={batches.error}
            onRetry={() => void batches.refetch()}
            title="批次没能加载"
          />
        ) : batches.data.batches.length === 0 ? (
          <EmptyState
            title="还没有批次"
            description="创建一个批次，选好任务与素材，再发布并派发。"
            action={
              <Link className="gz-button gz-button--primary" to="/m/batches">
                去创建批次
              </Link>
            }
          />
        ) : (
          <div className="xz-adm__cards">
            {batches.data.batches.map((b) => {
              const ratio = b.assignment_count === 0 ? 0 : b.recorded_count / b.assignment_count;
              return (
                <Link
                  key={b.id}
                  className="xz-bcard"
                  to={`/m/batches?batch=${encodeURIComponent(b.id)}`}
                >
                  <div className="xz-adm__tags">
                    <StatusTag icon={BATCH_STATE_ICON[b.state]} tone={BATCH_STATE_TONE[b.state]}>
                      {BATCH_STATE_LABEL[b.state]}
                    </StatusTag>
                  </div>
                  <p className="xz-bcard__title">{b.id}</p>
                  <p className="xz-bcard__meta">
                    <span>阶段 {b.phase}</span>
                    <span>{b.tasks.map((t) => TASK_LABEL[t]).join(" · ")}</span>
                  </p>
                  <ProgressBar
                    value={ratio}
                    label={`${b.id} 采集进度`}
                    showLabel
                    tone={ratio >= 1 ? "success" : "info"}
                  />
                  <p className="xz-bcard__meta">
                    <span>
                      <Icon id="ui-list-ordered" size={16} /> 派发 {b.assignment_count}
                    </span>
                    <span>
                      <Icon id="ui-microphone" size={16} /> 已录 {b.recorded_count}
                    </span>
                    <span>
                      <Icon id="ui-inbox" size={16} /> 录音 {b.recording_count}
                    </span>
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </Section>

      {/* --- 录音状态 + 门槛 -------------------------------------------- */}
      {summary.isPending ? (
        <LoadingBlock rows={3} label="正在加载质量数据" />
      ) : summary.isError ? (
        <ErrorNotice
          error={summary.error}
          onRetry={() => void summary.refetch()}
          title="质量与计数没能加载"
        />
      ) : (
        <>
          <Section
            title="录音状态"
            subtitle="按状态统计全部录音"
            actions={
              <Link className="gz-button" to="/m/qc">
                去质检
              </Link>
            }
          >
            <StatRow label="录音按状态计数" items={stateStats} />
            <p className="xz-adm__rowactions" style={{ marginTop: "var(--gz-sp-4)" }}>
              <StatusTag
                icon="ui-swap"
                tone={(summary.data.pending_arbitration ?? 0) > 0 ? "warning" : "neutral"}
              >
                待仲裁 {summary.data.pending_arbitration ?? 0} 条
              </StatusTag>
              <StatusTag icon="ui-check-circle">
                已汇总 {overall?.merged_recordings ?? 0} 条
              </StatusTag>
            </p>
          </Section>

          <Section
            title="质量门槛"
            subtitle="§10.2 四项门槛；每项都给图标、文字与数值，不只靠颜色"
            actions={
              <Link className="gz-button" to="/m/quality">
                质量看板
              </Link>
            }
          >
            <div className="xz-gates">
              <GateLight
                icon="ui-target"
                label="κ 一致性"
                value={overall?.kappa ?? null}
                pass={overall?.kappa_pass ?? null}
                gate={gates?.kappa ?? 0.8}
                compare=">="
                percent={false}
              />
              <GateLight
                icon="ui-percent"
                label="一致率"
                value={overall?.agreement_rate ?? null}
                pass={overall?.agreement_pass ?? null}
                gate={gates?.agreement_rate ?? 0.85}
                compare=">="
              />
              <GateLight
                icon="ui-swap"
                label="仲裁占比"
                value={overall?.arbitration_ratio ?? null}
                pass={overall?.arbitration_pass ?? null}
                gate={gates?.arbitration_ratio ?? 0.12}
                compare="<="
              />
              <GateLight
                icon="ui-star"
                label="黄金题命中"
                value={overall?.golden_hit_rate ?? null}
                pass={overall?.golden_pass ?? null}
                gate={gates?.golden_hit_rate ?? 0.9}
                compare=">="
              />
            </div>
          </Section>
        </>
      )}

      {/* --- 专家进度 --------------------------------------------------- */}
      <Section title="专家进度" subtitle="只统计条数与指标，不显示任何判断内容（AUTHZ.md）">
        {annotators.isPending ? (
          <LoadingBlock rows={2} label="正在加载专家进度" />
        ) : annotators.isError ? (
          <ErrorNotice
            error={annotators.error}
            onRetry={() => void annotators.refetch()}
            title="专家进度没能加载"
          />
        ) : annotators.data.annotators.length === 0 ? (
          <EmptyState
            title="还没有标注专家"
            description="到「账号」里给专家开通 annotator 角色。"
          />
        ) : (
          <ul className="xz-adm__proglist">
            {annotators.data.annotators.map((a) => (
              <li key={a.annotator_id} className="xz-adm__progitem">
                <span>{a.display_name}</span>
                <span className="gz-caption">
                  今日 {a.today} · 累计 {a.total} · κ {formatNumber(a.kappa)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <p className="xz-adm__rowactions">
        <Link className="gz-button" to="/m/materials">
          素材库
        </Link>
        <Link className="gz-button" to="/m/speakers">
          说话人
        </Link>
        <Link className="gz-button" to="/m/export">
          导出
        </Link>
        <Button
          variant="ghost"
          onClick={() => void summary.refetch()}
          leadingIcon={<Icon id="ui-refresh" size={16} />}
        >
          刷新
        </Button>
      </p>
    </>
  );
}
